import { useState } from 'react';
import { Info, ArrowRight } from '@phosphor-icons/react';
import type { ExpectedField, SheetColumnPreview } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { SimpleTooltip, IconTooltipButton } from '../../../components/ui/tooltip';
import { availableFieldsFor, isMappingComplete, missingRequiredFields, compactFieldLabel, cleanedFirstValue } from './importWizardHelpers';
import { WizardFooter } from './ImportWizardFooterSlot';

interface ImportMappingStepProps {
  sheetColumns: SheetColumnPreview[];
  expectedFields: ExpectedField[];
  rowCount: number;
  busy: boolean;
  onBack: () => void;
  onSubmit: (mapping: Record<string, string>) => Promise<void>;
}

/** First-column-wins de-dup for the initial suggested mapping, in case the
 *  server suggested the same field for two different headers. */
function initialMapping(sheetColumns: SheetColumnPreview[]): Record<string, string> {
  const seen = new Set<string>();
  const initial: Record<string, string> = {};
  for (const col of sheetColumns) {
    const suggestion = col.suggestedField;
    if (suggestion && !seen.has(suggestion)) {
      initial[col.header] = suggestion;
      seen.add(suggestion);
    } else {
      initial[col.header] = '';
    }
  }
  return initial;
}

export function ImportMappingStep({ sheetColumns, expectedFields, rowCount, busy, onBack, onSubmit }: ImportMappingStepProps) {
  const [mapping, setMapping] = useState<Record<string, string>>(() => initialMapping(sheetColumns));

  const complete = isMappingComplete(expectedFields, mapping);
  const missing = missingRequiredFields(expectedFields, mapping);
  const mappedCount = Object.values(mapping).filter(Boolean).length;

  const setColumnField = (header: string, value: string) => {
    setMapping((m) => {
      const next: Record<string, string> = { ...m, [header]: value };
      // Mutual exclusion: unassign this field from whichever OTHER column
      // held it, so at most one column ever maps to a given field.
      if (value) {
        for (const h of Object.keys(next)) {
          if (h !== header && next[h] === value) next[h] = '';
        }
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full border-collapse text-left text-[12px]">
          <thead>
            <tr className="bg-muted">
              <th className="border-b border-border px-3 py-2 font-semibold text-muted-foreground">Column in sheet</th>
              <th className="border-b border-border px-3 py-2 font-semibold text-muted-foreground">First value</th>
              <th className="border-b border-border px-3 py-2 font-semibold text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  Maps to
                  <SimpleTooltip
                    content="Skip leaves this column out of the import entirely — nothing from it is written, and it won't touch any existing value for creators already on the roster."
                  >
                    <IconTooltipButton label="What does Skip do?">
                      <Info size={12} className="text-muted-foreground" />
                    </IconTooltipButton>
                  </SimpleTooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sheetColumns.map((col) => {
              const options = availableFieldsFor(col.header, expectedFields, mapping);
              const current = mapping[col.header] ?? '';
              const mappedField = current ? expectedFields.find((f) => f.key === current) : undefined;
              const firstValue = mappedField
                ? cleanedFirstValue(mappedField.key, mappedField.isDeliverable ?? false, col.sampleValue ?? '')
                : (col.sampleValue ?? '');
              return (
                <tr key={col.header} className="border-b border-border last:border-b-0">
                  <td className="px-3 py-2 font-mono text-foreground">{col.header}</td>
                  <td className="px-3 py-2 text-muted-foreground">{firstValue || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-2">
                      <ArrowRight size={14} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                      <select
                        aria-label={`Maps to for ${col.header}`}
                        value={current}
                        onChange={(e) => setColumnField(col.header, e.target.value)}
                        className="w-48 rounded-lg border border-border bg-transparent px-3 py-1.5 text-body-sm-regular text-foreground"
                      >
                        <option value="">Skip</option>
                        {options.map((f) => (
                          <option key={f.key} value={f.key}>
                            {compactFieldLabel(f)}{f.required ? ' (required)' : ''}
                          </option>
                        ))}
                      </select>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {missing.length > 0 && (
        <p className="text-body-sm-regular text-rose-600">
          Missing required column{missing.length > 1 ? 's' : ''}: {missing.map((f) => f.label).join(', ')}
        </p>
      )}

      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="lg" disabled={busy} onClick={onBack}>
              Back
            </Button>
            <p className="text-body-sm-regular text-muted-foreground">
              {rowCount} data row{rowCount === 1 ? '' : 's'} · {mappedCount} column{mappedCount === 1 ? '' : 's'} mapped
            </p>
          </div>
          <Button
            type="button"
            variant="default"
            size="lg"
            loading={busy}
            disabled={!complete}
            onClick={() => { onSubmit(mapping).catch(() => {}); }}
          >
            {busy ? 'Saving…' : 'Continue'}
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
